import React, { useState, useEffect, useRef, useCallback } from 'react';
import { Button } from './ui/button';
import { Card, CardContent, CardHeader, CardTitle } from './ui/card';
import { Badge } from './ui/badge';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from './ui/dialog';
import { Sheet, SheetContent, SheetHeader, SheetTitle } from './ui/sheet';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from './ui/select';
import { ScrollArea } from './ui/scroll-area';
import { Separator } from './ui/separator';
import { Input } from './ui/input';
import {
  Phone, PhoneCall, PhoneOff, Bot, Loader2, CheckCircle,
  XCircle, AlertCircle, Clock, ArrowRight, Zap, MessageSquare,
  Target, ChevronDown, ChevronRight, Volume2, CalendarClock, X
} from 'lucide-react';
import { toast } from 'sonner';
import { format } from 'date-fns';
import { formatLocal } from '../lib/dateUtils';
import { voiceAPI } from '../lib/api';

const CALL_TYPES = {
  discovery: { label: 'Discovery', icon: Target, color: 'text-blue-600', bg: 'bg-blue-50' },
  demo_scheduling: { label: 'Demo Scheduling', icon: Zap, color: 'text-purple-600', bg: 'bg-purple-50' },
  followup: { label: 'Follow-up', icon: ArrowRight, color: 'text-amber-600', bg: 'bg-amber-50' },
  closing: { label: 'Closing', icon: CheckCircle, color: 'text-green-600', bg: 'bg-green-50' },
  re_engagement: { label: 'Re-engagement', icon: Phone, color: 'text-teal-600', bg: 'bg-teal-50' },
};

const STATUS_CONFIG = {
  initiated: { label: 'Initiated', icon: Phone, cls: 'bg-blue-100 text-blue-800 dark:bg-blue-900/30 dark:text-blue-400' },
  ringing: { label: 'Ringing', icon: PhoneCall, cls: 'bg-yellow-100 text-yellow-800 dark:bg-yellow-900/30 dark:text-yellow-400' },
  'in-progress': { label: 'In Progress', icon: Volume2, cls: 'bg-indigo-100 text-indigo-800 dark:bg-indigo-900/30 dark:text-indigo-400' },
  completed: { label: 'Completed', icon: CheckCircle, cls: 'bg-green-100 text-green-800 dark:bg-green-900/30 dark:text-green-400' },
  failed: { label: 'Failed', icon: XCircle, cls: 'bg-red-100 text-red-800 dark:bg-red-900/30 dark:text-red-400' },
  'no-answer': { label: 'No Answer', icon: PhoneOff, cls: 'bg-muted text-foreground' },
};

// ========================
// BRAIN CALL BUTTON
// ========================

export function BrainCallButton({ leadId, leadName, leadPhone, onCallInitiated, size = 'sm' }) {
  const [dialogOpen, setDialogOpen] = useState(false);
  const [callType, setCallType] = useState('auto');
  const [loading, setLoading] = useState(false);
  const [scheduleMode, setScheduleMode] = useState(false);
  const [scheduledAt, setScheduledAt] = useState('');
  const [optimalTime, setOptimalTime] = useState(null);
  const [loadingOptimal, setLoadingOptimal] = useState(false);

  const handleInitiate = async () => {
    if (!leadPhone) {
      toast.error('Lead has no phone number');
      return;
    }
    setLoading(true);
    try {
      // When 'auto' is selected, pass null to let the backend auto-detect
      const typeParam = callType === 'auto' ? null : callType;
      const res = await voiceAPI.initiateBrainCall(leadId, typeParam || null);
      const data = res.data;
      const summary = data.ai_summary || data.brief_summary || `Calling ${data.to_number}`;
      toast.success(`${CALL_TYPES[data.call_type]?.label || 'AI'} call ${data.status === 'completed' ? 'completed' : 'initiated'} — ${leadName}`, {
        description: summary,
        duration: 6000,
      });
      setDialogOpen(false);
      if (onCallInitiated) onCallInitiated(data);
    } catch (err) {
      const detail = err.response?.data?.detail || 'Failed to initiate call';
      toast.error(detail);
    } finally {
      setLoading(false);
    }
  };

  const handleSchedule = async () => {
    if (!leadPhone) { toast.error('Lead has no phone number'); return; }
    setLoading(true);
    try {
      const typeParam = callType === 'auto' ? null : callType;
      const res = await voiceAPI.scheduleCall(leadId, {
        call_type: typeParam,
        scheduled_at: scheduledAt || null,
      });
      const data = res.data;
      const time = data.scheduled_at ? formatLocal(data.scheduled_at, 'MMM dd, h:mm a') : 'optimal time';
      toast.success(`Call scheduled for ${leadName}`, { description: `At ${time} (${data.timezone})` });
      setDialogOpen(false);
      setScheduleMode(false);
      if (onCallInitiated) onCallInitiated(data);
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to schedule call');
    } finally {
      setLoading(false);
    }
  };

  const fetchOptimalTime = async () => {
    setLoadingOptimal(true);
    try {
      const res = await voiceAPI.getOptimalTime(leadId);
      setOptimalTime(res.data);
    } catch { /* ignore */ }
    finally { setLoadingOptimal(false); }
  };

  const openScheduleMode = () => {
    setScheduleMode(true);
    fetchOptimalTime();
  };

  return (
    <>
      <Button
        size={size}
        variant="default"
        onClick={() => setDialogOpen(true)}
        disabled={!leadPhone}
        data-testid="brain-call-button"
        className="bg-violet-600 hover:bg-violet-700 text-white"
      >
        <Bot className="h-4 w-4 mr-1.5" />
        AI Call
      </Button>

      <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Bot className="h-5 w-5 text-violet-600" />
              AI Brain Call
            </DialogTitle>
            <DialogDescription>
              The AI Brain will autonomously call {leadName || 'this lead'} using the best strategy.
            </DialogDescription>
          </DialogHeader>

          <div className="space-y-4 py-2">
            <div className="rounded-lg bg-violet-50 border border-violet-200 p-3 text-sm">
              <p className="font-medium text-violet-800">How it works:</p>
              <ul className="mt-1 text-violet-700 text-xs space-y-1">
                <li>1. Brain analyzes lead context &amp; generates a call brief</li>
                <li>2. ElevenLabs AI agent places the call via Twilio</li>
                <li>3. Post-call: AI generates summary &amp; updates lead status</li>
              </ul>
            </div>

            <div className="space-y-2">
              <label className="text-sm font-medium">Call Type (optional)</label>
              <Select value={callType} onValueChange={setCallType}>
                <SelectTrigger data-testid="call-type-select">
                  <SelectValue placeholder="Auto-detect (recommended)" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="auto">Auto-detect</SelectItem>
                  {Object.entries(CALL_TYPES).map(([key, info]) => (
                    <SelectItem key={key} value={key}>{info.label}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            {/* Schedule Mode */}
            {scheduleMode && (
              <div className="space-y-3 rounded-lg border border-blue-200 bg-blue-50/50 p-3">
                <div className="flex items-center justify-between">
                  <p className="text-sm font-medium flex items-center gap-1.5">
                    <CalendarClock className="h-4 w-4 text-blue-600" />
                    Schedule for later
                  </p>
                  <Button variant="ghost" size="sm" className="h-6 w-6 p-0" onClick={() => setScheduleMode(false)}>
                    <X className="h-3 w-3" />
                  </Button>
                </div>
                {loadingOptimal ? (
                  <div className="flex items-center gap-2 text-xs text-muted-foreground">
                    <Loader2 className="h-3 w-3 animate-spin" /> Calculating optimal time...
                  </div>
                ) : optimalTime && (
                  <div className="text-xs text-blue-700 bg-blue-100 rounded p-2">
                    Recommended: <strong>{optimalTime.window}</strong> ({optimalTime.timezone})
                    <span className="text-blue-500 ml-1">— {optimalTime.source === 'engagement_history' ? 'based on engagement' : 'business hours'}</span>
                  </div>
                )}
                <div className="space-y-1">
                  <label className="text-xs font-medium">Custom date/time (optional)</label>
                  <Input
                    type="datetime-local"
                    value={scheduledAt}
                    onChange={(e) => setScheduledAt(e.target.value)}
                    className="text-sm"
                    data-testid="schedule-datetime-input"
                  />
                  <p className="text-xs text-muted-foreground">Leave blank to use the AI-recommended optimal time</p>
                </div>
              </div>
            )}

            <div className="rounded-lg bg-muted/50 p-3 text-sm flex items-center gap-2">
              <AlertCircle className="h-4 w-4 text-muted-foreground shrink-0" />
              <span className="text-muted-foreground text-xs">
                Credits will be deducted based on call type (0.50 - 1.00 credits).
              </span>
            </div>
          </div>

          <DialogFooter className="flex-col sm:flex-row gap-2">
            <Button variant="outline" onClick={() => { setDialogOpen(false); setScheduleMode(false); }}>Cancel</Button>
            {!scheduleMode ? (
              <>
                <Button
                  variant="outline"
                  onClick={openScheduleMode}
                  disabled={loading || !leadPhone}
                  data-testid="brain-call-schedule-btn"
                  className="border-blue-300 text-blue-700 hover:bg-blue-50"
                >
                  <CalendarClock className="h-4 w-4 mr-2" />Schedule
                </Button>
                <Button
                  onClick={handleInitiate}
                  disabled={loading || !leadPhone}
                  data-testid="brain-call-initiate-btn"
                  className="bg-violet-600 hover:bg-violet-700"
                >
                  {loading ? (
                    <><Loader2 className="h-4 w-4 mr-2 animate-spin" />Calling...</>
                  ) : (
                    <><PhoneCall className="h-4 w-4 mr-2" />Call Now</>
                  )}
                </Button>
              </>
            ) : (
              <Button
                onClick={handleSchedule}
                disabled={loading || !leadPhone}
                data-testid="brain-call-confirm-schedule-btn"
                className="bg-blue-600 hover:bg-blue-700"
              >
                {loading ? (
                  <><Loader2 className="h-4 w-4 mr-2 animate-spin" />Scheduling...</>
                ) : (
                  <><CalendarClock className="h-4 w-4 mr-2" />Confirm Schedule</>
                )}
              </Button>
            )}
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </>
  );
}

// ========================
// BRAIN CALL PANEL
// ========================

export function BrainCallPanel({ leadId, leadName, leadPhone, refreshKey }) {
  const [isOpen, setIsOpen] = useState(false);
  const [calls, setCalls] = useState([]);
  const [scheduledCalls, setScheduledCalls] = useState([]);
  const [loading, setLoading] = useState(false);
  const [selectedCall, setSelectedCall] = useState(null);
  const [detailOpen, setDetailOpen] = useState(false);
  const [hangingUpId, setHangingUpId] = useState(null);
  const pollingRef = useRef(null);

  const loadCalls = useCallback(async () => {
    try {
      const [callsRes, queueRes] = await Promise.all([
        voiceAPI.getBrainCallsForLead(leadId),
        voiceAPI.getCallQueueForLead(leadId).catch(() => ({ data: { calls: [] } })),
      ]);
      setCalls(callsRes.data.calls || []);
      setScheduledCalls(queueRes.data.calls || []);
    } catch {
      // silent
    }
  }, [leadId]);

  const handleHangup = useCallback(async (callId) => {
    setHangingUpId(callId);
    try {
      await voiceAPI.hangupBrainCall(callId);
      toast.success('Call ended');
      await loadCalls();
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Could not end the call');
    } finally {
      setHangingUpId(null);
    }
  }, [loadCalls]);

  useEffect(() => {
    if (isOpen) {
      setLoading(true);
      loadCalls().finally(() => setLoading(false));
    }
  }, [isOpen, leadId, loadCalls, refreshKey]);

  // Poll for active calls
  useEffect(() => {
    const hasActive = calls.some(c => ['initiated', 'ringing', 'in-progress'].includes(c.status));
    const hasScheduled = scheduledCalls.length > 0;
    if ((hasActive || hasScheduled) && isOpen) {
      pollingRef.current = setInterval(loadCalls, 5000);
    } else if (pollingRef.current) {
      clearInterval(pollingRef.current);
      pollingRef.current = null;
    }
    return () => { if (pollingRef.current) clearInterval(pollingRef.current); };
  }, [calls, scheduledCalls, isOpen, loadCalls]);

  const handleCallInitiated = (data) => {
    setIsOpen(true);
    loadCalls();
  };

  const handleCancelScheduled = async (queueId) => {
    try {
      await voiceAPI.cancelScheduledCall(queueId);
      toast.success('Scheduled call cancelled');
      loadCalls();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to cancel');
    }
  };

  const getCallTypeInfo = (type) => CALL_TYPES[type] || CALL_TYPES.discovery;
  const getStatusInfo = (status) => STATUS_CONFIG[status] || STATUS_CONFIG.initiated;

  const totalCount = calls.length + scheduledCalls.length;

  return (
    <Card data-testid="brain-call-panel" className="border border-border rounded-lg overflow-hidden">
      <div
        className="flex items-center justify-between px-4 py-3 cursor-pointer hover:bg-muted/50 transition-colors"
        onClick={() => setIsOpen(!isOpen)}
      >
        <div className="flex items-center gap-2">
          {isOpen ? <ChevronDown className="h-4 w-4 text-muted-foreground" /> : <ChevronRight className="h-4 w-4 text-muted-foreground" />}
          <Bot className="h-5 w-5 text-violet-600" />
          <div>
            <span className="text-sm font-semibold">AI Brain Calls</span>
            {totalCount > 0 && (
              <span className="text-xs text-muted-foreground ml-2">
                {totalCount} call{totalCount !== 1 ? 's' : ''}
                {scheduledCalls.length > 0 && ` (${scheduledCalls.length} scheduled)`}
              </span>
            )}
          </div>
        </div>
        <div onClick={(e) => e.stopPropagation()}>
          <BrainCallButton
            leadId={leadId}
            leadName={leadName}
            leadPhone={leadPhone}
            onCallInitiated={handleCallInitiated}
          />
        </div>
      </div>

      {isOpen && (
        <CardContent className="pt-0 pb-4">
          {loading ? (
            <div className="flex items-center justify-center py-6">
              <Loader2 className="h-5 w-5 animate-spin text-muted-foreground" />
            </div>
          ) : totalCount === 0 ? (
            <div className="text-center py-6 text-muted-foreground">
              <PhoneOff className="h-8 w-8 mx-auto mb-2 opacity-50" />
              <p className="text-sm">No AI Brain calls yet</p>
              <p className="text-xs mt-1">Click "AI Call" to let the Brain make a call</p>
            </div>
          ) : (
            <ScrollArea className="max-h-[400px]">
              <div className="space-y-2">
                {/* Scheduled calls first */}
                {scheduledCalls.map((sc) => {
                  const typeInfo = getCallTypeInfo(sc.call_type);
                  const TypeIcon = typeInfo.icon;
                  return (
                    <div
                      key={sc.id}
                      className="flex items-center gap-3 p-3 rounded-lg border border-blue-200 bg-blue-50/50 dark:border-blue-800/50 dark:bg-blue-950/20"
                      data-testid={`scheduled-call-${sc.id}`}
                    >
                      <div className={`p-2 rounded-full ${typeInfo.bg}`}>
                        <CalendarClock className={`h-4 w-4 ${typeInfo.color}`} />
                      </div>
                      <div className="flex-1 min-w-0">
                        <div className="flex items-center gap-2">
                          <span className="text-sm font-medium">{typeInfo.label} Call</span>
                          <Badge variant="outline" className="text-[10px] border-blue-300 text-blue-700 bg-blue-100">
                            <Clock className="h-2.5 w-2.5 mr-1" />Scheduled
                          </Badge>
                        </div>
                        <p className="text-xs text-muted-foreground mt-0.5">
                          {sc.scheduled_at ? formatLocal(sc.scheduled_at, 'MMM dd, h:mm a') : 'Optimal time'}
                          {sc.timezone && ` (${sc.timezone})`}
                        </p>
                      </div>
                      <Button
                        variant="ghost"
                        size="sm"
                        className="h-7 text-xs text-red-600 hover:text-red-700 hover:bg-red-50"
                        onClick={() => handleCancelScheduled(sc.id)}
                        data-testid={`cancel-scheduled-${sc.id}`}
                      >
                        <XCircle className="h-3.5 w-3.5 mr-1" />Cancel
                      </Button>
                    </div>
                  );
                })}

                {/* Executed calls */}
                {calls.map((call) => {
                  const typeInfo = getCallTypeInfo(call.call_type);
                  const statusInfo = getStatusInfo(call.status || call.twilio_status);
                  const StatusIcon = statusInfo.icon;
                  const isActive = ['initiated', 'ringing', 'in-progress'].includes(call.status);

                  return (
                    <div
                      key={call.id}
                      className={`border rounded-lg p-3 hover:bg-muted/30 transition-colors cursor-pointer ${isActive ? 'border-violet-300 bg-violet-50/50 dark:border-violet-700 dark:bg-violet-950/30' : 'border-border'}`}
                      onClick={() => { setSelectedCall(call); setDetailOpen(true); }}
                      data-testid={`brain-call-item-${call.id}`}
                    >
                      <div className="flex items-center justify-between">
                        <div className="flex items-center gap-2.5">
                          <div className={`h-8 w-8 rounded-full flex items-center justify-center ${typeInfo.bg}`}>
                            <PhoneCall className={`h-4 w-4 ${typeInfo.color}`} />
                          </div>
                          <div>
                            <p className="text-sm font-medium">{typeInfo.label} Call</p>
                            <p className="text-xs text-muted-foreground">
                              {call.to_number} {call.created_at && `· ${formatLocal(call.created_at, 'MMM dd, HH:mm')}`}
                            </p>
                          </div>
                        </div>
                        <div className="flex items-center gap-2">
                          <Badge className={`text-xs ${statusInfo.cls}`}>
                            <StatusIcon className="h-3 w-3 mr-1" />
                            {statusInfo.label}
                          </Badge>
                          {isActive && <Loader2 className="h-3 w-3 animate-spin text-violet-500" />}
                          {isActive && (
                            <Button
                              variant="destructive"
                              size="sm"
                              className="h-7 px-2"
                              onClick={(e) => { e.stopPropagation(); handleHangup(call.id); }}
                              disabled={hangingUpId === call.id}
                              data-testid={`hangup-call-${call.id}`}
                            >
                              {hangingUpId === call.id
                                ? <Loader2 className="h-3 w-3 animate-spin" />
                                : <><PhoneOff className="h-3 w-3 mr-1" />End</>}
                            </Button>
                          )}
                        </div>
                      </div>

                      {/* Show AI summary if completed */}
                      {call.ai_summary && (
                        <div className="mt-2 pt-2 border-t border-border">
                          <p className="text-xs text-muted-foreground line-clamp-2">{call.ai_summary}</p>
                        </div>
                      )}
                      {call.outcome && (
                        <div className="mt-1.5 flex items-center gap-2">
                          <Badge variant="outline" className="text-xs capitalize">
                            {call.outcome === 'positive' ? '+ Positive' : call.outcome === 'negative' ? '- Negative' : '~ Neutral'}
                          </Badge>
                          {call.next_action && (
                            <span className="text-xs text-muted-foreground truncate">Next: {call.next_action}</span>
                          )}
                        </div>
                      )}
                    </div>
                  );
                })}
              </div>
            </ScrollArea>
          )}
        </CardContent>
      )}

      {/* Call Details Dialog */}
      {selectedCall && (
        <BrainCallDetailDialog
          call={selectedCall}
          open={detailOpen}
          onOpenChange={setDetailOpen}
        />
      )}
    </Card>
  );
}

// ========================
// BRAIN CALL DETAIL DIALOG
// ========================

function BrainCallDetailDialog({ call, open, onOpenChange }) {
  const [liveCall, setLiveCall] = useState(call);
  const pollingRef = useRef(null);

  // Poll for status updates on active calls
  useEffect(() => {
    if (open && call?.id) {
      setLiveCall(call);
      const isActive = ['initiated', 'ringing', 'in-progress'].includes(call.status);
      if (isActive) {
        const poll = async () => {
          try {
            const res = await voiceAPI.getBrainCallStatus(call.id);
            setLiveCall(res.data);
            if (['completed', 'failed', 'no-answer'].includes(res.data.status || res.data.twilio_status)) {
              clearInterval(pollingRef.current);
            }
          } catch { /* ignore */ }
        };
        pollingRef.current = setInterval(poll, 4000);
        return () => clearInterval(pollingRef.current);
      }
    }
    return () => { if (pollingRef.current) clearInterval(pollingRef.current); };
  }, [open, call]);

  if (!call) return null;

  const c = liveCall || call;
  const typeInfo = CALL_TYPES[c.call_type] || CALL_TYPES.discovery;
  const statusInfo = STATUS_CONFIG[c.status || c.twilio_status] || STATUS_CONFIG.initiated;
  const StatusIcon = statusInfo.icon;
  const brief = c.brief || {};
  const isActive = ['initiated', 'ringing', 'in-progress'].includes(c.status);

  return (
    <Sheet open={open} onOpenChange={onOpenChange}>
      <SheetContent className="sm:max-w-lg overflow-y-auto" data-testid="brain-call-detail-sheet">
        <SheetHeader>
          <SheetTitle className="flex items-center gap-2">
            <Bot className="h-5 w-5 text-violet-500" />
            {typeInfo.label} Call Details
          </SheetTitle>
        </SheetHeader>

        <div className="space-y-4 mt-4">
          {/* Status Banner — dark-theme safe */}
          <div className={`rounded-lg p-3 flex items-center justify-between ${
            isActive 
              ? 'bg-violet-50 border border-violet-200 dark:bg-violet-950/40 dark:border-violet-700' 
              : c.status === 'completed' 
                ? 'bg-emerald-50 border border-emerald-200 dark:bg-emerald-950/40 dark:border-emerald-700' 
                : 'bg-red-50 border border-red-200 dark:bg-red-950/40 dark:border-red-700'
          }`}>
            <div className="flex items-center gap-2">
              <StatusIcon className={`h-5 w-5 ${
                isActive ? 'text-violet-600 dark:text-violet-400' 
                : c.status === 'completed' ? 'text-emerald-600 dark:text-emerald-400' 
                : 'text-red-600 dark:text-red-400'
              }`} />
              <div>
                <p className="font-medium text-sm">{statusInfo.label}</p>
                <p className="text-xs text-muted-foreground">{c.to_number} · {c.mode} mode</p>
              </div>
            </div>
            {isActive && <Loader2 className="h-4 w-4 animate-spin text-violet-500" />}
            {c.duration_seconds > 0 && (
              <Badge variant="outline"><Clock className="h-3 w-3 mr-1" />{c.duration_seconds}s</Badge>
            )}
          </div>

          {/* AI Summary */}
          {c.ai_summary && (
            <div className="space-y-1">
              <p className="text-xs font-semibold uppercase text-muted-foreground">AI Summary</p>
              <p className="text-sm bg-muted/50 rounded-lg p-3 dark:bg-muted/30">{c.ai_summary}</p>
            </div>
          )}

          {/* Outcome & Next Action */}
          {c.outcome && (
            <div className="grid grid-cols-2 gap-3">
              <div className="rounded-lg border border-border p-3">
                <p className="text-xs font-semibold uppercase text-muted-foreground mb-1">Outcome</p>
                <Badge variant="outline" className={`capitalize ${
                  c.outcome === 'positive' ? 'border-emerald-300 text-emerald-700 dark:border-emerald-700 dark:text-emerald-400' 
                  : c.outcome === 'negative' ? 'border-red-300 text-red-700 dark:border-red-700 dark:text-red-400' 
                  : 'border-amber-300 text-amber-700 dark:border-amber-700 dark:text-amber-400'
                }`}>
                  {c.outcome}
                </Badge>
              </div>
              <div className="rounded-lg border border-border p-3">
                <p className="text-xs font-semibold uppercase text-muted-foreground mb-1">Next Action</p>
                <p className="text-sm">{c.next_action || 'N/A'}</p>
              </div>
            </div>
          )}

          {/* Key Insights */}
          {c.key_insights && c.key_insights.length > 0 && (
            <div>
              <p className="text-xs font-semibold uppercase text-muted-foreground mb-1">Key Insights</p>
              <div className="flex flex-wrap gap-1.5">
                {c.key_insights.map((insight, i) => (
                  <Badge key={i} variant="secondary" className="text-xs">{insight}</Badge>
                ))}
              </div>
            </div>
          )}

          <Separator />

          {/* Call Brief (Collapsible) */}
          <BriefSection brief={brief} />

          {/* Meta */}
          <div className="text-xs text-muted-foreground space-y-1.5 bg-muted/30 rounded-lg p-3">
            <div className="flex justify-between"><span>Call ID</span><span className="font-mono">{c.id?.slice(0, 12)}...</span></div>
            <div className="flex justify-between"><span>Call Type</span><span>{typeInfo.label}</span></div>
            <div className="flex justify-between"><span>Credit Cost</span><span>${c.credit_cost?.toFixed(2) || '0.50'}</span></div>
            {c.created_at && <div className="flex justify-between"><span>Started</span><span>{formatLocal(c.created_at)}</span></div>}
          </div>
        </div>

        <div className="mt-4">
          <Button variant="outline" className="w-full" onClick={() => onOpenChange(false)}>Close</Button>
        </div>
      </SheetContent>
    </Sheet>
  );
}

// ========================
// BRIEF SECTION (expandable)
// ========================

function BriefSection({ brief }) {
  const [expanded, setExpanded] = useState(false);
  if (!brief || (!brief.greeting && !brief.purpose_statement)) return null;

  return (
    <div>
      <button
        className="flex items-center gap-1 text-xs font-semibold uppercase text-muted-foreground hover:text-foreground transition-colors"
        onClick={() => setExpanded(!expanded)}
      >
        {expanded ? <ChevronDown className="h-3 w-3" /> : <ChevronRight className="h-3 w-3" />}
        Call Brief
      </button>
      {expanded && (
        <div className="mt-2 space-y-2 bg-muted/30 rounded-lg p-3 text-sm">
          {brief.greeting && <div><span className="font-medium text-xs text-muted-foreground">Greeting:</span><p>{brief.greeting}</p></div>}
          {brief.purpose_statement && <div><span className="font-medium text-xs text-muted-foreground">Purpose:</span><p>{brief.purpose_statement}</p></div>}
          {brief.value_proposition && <div><span className="font-medium text-xs text-muted-foreground">Value Prop:</span><p>{brief.value_proposition}</p></div>}
          {brief.closing_action && <div><span className="font-medium text-xs text-muted-foreground">Closing Action:</span><p>{brief.closing_action}</p></div>}
          {brief.discovery_questions && brief.discovery_questions.length > 0 && (
            <div>
              <span className="font-medium text-xs text-muted-foreground">Discovery Questions:</span>
              <ul className="list-disc list-inside text-xs mt-1 space-y-0.5">
                {brief.discovery_questions.map((q, i) => <li key={i}>{q}</li>)}
              </ul>
            </div>
          )}
        </div>
      )}
    </div>
  );
}
